import { Home, Briefcase, Users, Ruler, FileText, Shield, Settings, Target, Package, Mail, Plane, Activity, CalendarDays, Sparkles, Workflow, BarChart, HelpCircle, MessageSquare } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
  SidebarHeader,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const coreItems = [
  { title: "Home", url: "/", icon: Home, badge: null },
  { title: "Sales Hub", url: "/sales-hub", icon: Target, badge: "AI" },
  { title: "Jobs", url: "/jobs", icon: Briefcase, badge: null },
  { title: "Email", url: "/email", icon: Mail, badge: null },
  { title: "Team Chat", url: "/team-chat", icon: MessageSquare, badge: null },
];

const toolsItems = [
  { title: "Measure", url: "/measurements", icon: Ruler },
  { title: "Drone Inspection", url: "/drone", icon: Plane },
  { title: "Catalog", url: "/catalog", icon: Package },
  { title: "Forms/Templates", url: "/templates", icon: FileText },
  { title: "Calendar", url: "/calendar", icon: CalendarDays },
];

const productionItems = [
  { title: "Dispatch", url: "/dispatch", icon: CalendarDays },
  { title: "Crews", url: "/crews", icon: Users },
  { title: "Materials", url: "/material-orders", icon: Package },
  { title: "Workflows", url: "/workflow-automation", icon: Workflow },
];

  const systemItems = [
    { title: "Reports", url: "/reports", icon: BarChart },
    { title: "System Monitor", url: "/system-monitor", icon: Activity },
    { title: "Help", url: "/help", icon: HelpCircle },
    { title: "Platform Admin", url: "/platform-admin", icon: Shield },
    { title: "Settings", url: "/settings", icon: Settings },
  ];

export function AppSidebar() {
  const { state } = useSidebar();
  const location = useLocation();
  const collapsed = state === "collapsed";

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const renderMenuItem = (item: any, active: boolean) => {
    const menuButton = (
      <SidebarMenuButton 
        asChild 
        isActive={active}
        className={cn(
          "group h-11 transition-all duration-200 hover:bg-accent/50 mx-1",
          collapsed ? "w-11 justify-center rounded-lg p-0" : "px-3 rounded-lg",
          active && "bg-gradient-to-r from-primary to-secondary shadow-md hover:shadow-lg hover:from-primary/90 hover:to-secondary/90"
        )}
      >
        <NavLink 
          to={item.url} 
          end={item.url === "/"} 
          className={cn(
            "flex items-center w-full",
            collapsed ? "justify-center" : "gap-3"
          )}
        >
          <item.icon 
            className={cn(
              "flex-shrink-0 transition-all duration-200 w-5 h-5",
              active ? "text-primary-foreground" : "text-primary group-hover:text-primary-glow"
            )}
          />
          {!collapsed && (
            <>
              <span className={cn(
                "flex-1 text-sm font-medium transition-all duration-200",
                active ? "text-primary-foreground font-semibold" : "text-white group-hover:text-white/90"
              )}>
                {item.title}
              </span>
              {item.badge && (
                <span className="px-1.5 py-0.5 text-[9px] font-bold rounded-md bg-accent text-accent-foreground uppercase tracking-wide">
                  {item.badge}
                </span>
              )}
            </>
          )}
        </NavLink>
      </SidebarMenuButton>
    );

    if (collapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            {menuButton}
          </TooltipTrigger>
          <TooltipContent side="right" className="font-medium">
            {item.title}
          </TooltipContent>
        </Tooltip>
      );
    }

    return menuButton;
  };

  return (
    <Sidebar 
      collapsible="icon" 
      className={cn(
        "sidebar-premium z-50 border-r border-border/40 transition-all duration-300 backdrop-blur-sm",
        collapsed ? "w-[60px]" : "md:w-64"
      )}
    >
      <SidebarHeader className={cn(
        "h-[72px] border-b border-border/40 flex items-center transition-all duration-300",
        collapsed ? "justify-center px-2" : "px-5"
      )}>
        <div className={cn("flex items-center transition-all duration-300", collapsed ? "justify-center" : "gap-3")}>
          <div className={cn(
            "rounded-lg bg-gradient-to-br from-primary via-secondary to-accent flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-105",
            collapsed ? "w-9 h-9" : "w-11 h-11"
          )}>
            <Sparkles className={cn("text-primary-foreground transition-all duration-300", collapsed ? "w-4 h-4" : "w-5 h-5")} />
          </div>
          {!collapsed && (
            <div className="flex flex-col gap-0.5">
              <span className="text-lg font-bold text-white">
                RoofGenius
              </span>
              <span className="text-[9px] text-primary-glow font-semibold tracking-[0.15em] uppercase">
                AI Platform
              </span>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className={cn("gap-0 py-4 transition-all", collapsed ? "px-0" : "px-2.5")}>
        {/* Core */}
        <SidebarGroup>
          {!collapsed && (
            <SidebarGroupLabel className="px-3 text-[10px] font-bold uppercase tracking-[0.12em] text-primary-glow mb-2">
              Core
            </SidebarGroupLabel>
          )}
          <SidebarGroupContent>
            <SidebarMenu className="gap-1">
              {coreItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  {renderMenuItem(item, isActive(item.url))}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <div className={cn("my-3 border-t border-border/30 transition-all duration-300", collapsed ? "mx-2" : "mx-3")} />

        {/* Tools */}
        <SidebarGroup>
          {!collapsed && (
            <SidebarGroupLabel className="px-3 text-[10px] font-bold uppercase tracking-[0.12em] text-primary-glow mb-2">
              Tools
            </SidebarGroupLabel>
          )}
          <SidebarGroupContent>
            <SidebarMenu className="gap-1">
              {toolsItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  {renderMenuItem(item, isActive(item.url))}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <div className={cn("my-3 border-t border-border/30 transition-all duration-300", collapsed ? "mx-2" : "mx-3")} />

        {/* Production */}
        <SidebarGroup>
          {!collapsed && (
            <SidebarGroupLabel className="px-3 text-[10px] font-bold uppercase tracking-[0.12em] text-primary-glow mb-2">
              Production
            </SidebarGroupLabel>
          )}
          <SidebarGroupContent>
            <SidebarMenu className="gap-1">
              {productionItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  {renderMenuItem(item, isActive(item.url))}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <div className={cn("my-3 border-t border-border/30 transition-all duration-300", collapsed ? "mx-2" : "mx-3")} />

        {/* System */}
        <SidebarGroup className="mt-auto">
          {!collapsed && (
            <SidebarGroupLabel className="px-3 text-[10px] font-bold uppercase tracking-[0.12em] text-primary-glow mb-2">
              System
            </SidebarGroupLabel>
          )}
          <SidebarGroupContent>
            <SidebarMenu className="gap-1">
              {systemItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  {renderMenuItem(item, isActive(item.url))}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
